import { useMemo } from 'react';

import { Image01Icon, PaintBoardIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { DEFAULT_BRANDING_COLORS } from 'hooks/branding-style-variables';
import { useColorContrast } from 'hooks/color-contrast-checker';
import { Controller, useWatch } from 'react-hook-form';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { ColorContrastCard } from 'components/ColorContrastCard/ColorContrastCard';
import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ColorInput, ImagePicker, Input, InputGroup } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import { useGetUploadQuery } from 'api/upload-api';

import GeneralPreview from './GeneralPreview';

export const General = ({
  brand,
  errors,
  control,
  register,
  backgroundColor = 'background-secondary',
}) => {
  const colorButtonValue = useWatch({ control, name: 'color_button' });
  const colorButtonTextValue = useWatch({ control, name: 'color_button_text' });
  const colorLinkValue = useWatch({ control, name: 'color_link' });
  const brandLogoId = useWatch({ control, name: 'brand_logo_id' });
  const { data: brandLogoUpload } = useGetUploadQuery(brandLogoId, { skip: !brandLogoId });
  const resolvedColorButton = colorButtonValue || DEFAULT_BRANDING_COLORS.color_button;
  const resolvedColorButtonText = colorButtonTextValue || DEFAULT_BRANDING_COLORS.color_button_text;
  const resolvedColorLink = colorLinkValue || DEFAULT_BRANDING_COLORS.color_link;
  const { ratio } = useColorContrast(resolvedColorLink, '#fff');

  const previewBrand = useMemo(() => {
    if (!brand) return null;
    const hasLogoId = brandLogoId != null && brandLogoId !== '';
    const resolvedBrandLogo = !hasLogoId
      ? null
      : (brandLogoUpload?.url ??
        (String(brandLogoId) === String(brand.brand_logo_id) ? brand.brand_logo : undefined));
    return {
      ...brand,
      brand_logo_id: brandLogoId,
      color_button: resolvedColorButton,
      color_button_text: resolvedColorButtonText,
      color_link: resolvedColorLink,
      email_logo_alignment: brand.email_logo_alignment ?? 'center',
      brand_logo: resolvedBrandLogo,
    };
  }, [
    brand,
    brandLogoId,
    brandLogoUpload,
    resolvedColorButton,
    resolvedColorButtonText,
    resolvedColorLink,
  ]);

  return (
    <BrandingTabSplitLayout
      key={brand?.id}
      shellClassName={backgroundColor}
      preview={<GeneralPreview brand={previewBrand} />}>
      <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm">
        <InputGroup
          title="Display Name"
          description="Public-facing name shown to candidates and external users."
          noMargin>
          <Input
            register={register}
            validators={{ required: true }}
            name="brand_name"
            placeholder="Checkmate"
            error={errors?.brand_name?.type}
          />
        </InputGroup>
      </section>

      <section className="flex flex-col gap-8 rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm">
        <article className="flex flex-col gap-4">
          <div className="flex gap-2">
            <HugeiconsIcon icon={Image01Icon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2 mb-4">
              <span className="text-2xl font-medium text-neutral-7">Brand Logo</span>
              <p className="text-xl text-text-tertiary">
                Upload your logo in PNG, JPG, or SVG format (max 5MB)
              </p>
            </div>
          </div>
          <Controller
            control={control}
            name="brand_logo_id"
            render={({ field: { onChange, value } }) => (
              <ImagePicker onChange={onChange} value={value} />
            )}
          />
        </article>
        <article className="flex flex-col gap-4">
          <div className="flex gap-2">
            <HugeiconsIcon icon={PaintBoardIcon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2 mb-4">
              <span className="text-2xl font-medium text-neutral-7">Report brand colors</span>
              <p className="text-xl text-text-tertiary">Define your report&apos;s color palette</p>
            </div>
          </div>
          <div className="flex flex-col gap-4 border border-neutral-4 rounded-3xl p-4 mb-4">
            <div className="flex gap-2">
              <IconComponent
                icon={PaintBoardIcon}
                size="medium"
                type="secondary"
                className="shrink-0"
              />
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-medium text-neutral-7">Brand</span>
                <p className="text-xl text-text-tertiary">
                  These colors are used for branded elements throughout all experiences, such as
                  emails, candidate experience, referee experience, candidate portal and few other
                  touchpoints.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <InputGroup title="Button color (Primary)" noMargin>
                <ColorInput
                  name="color_button"
                  register={register}
                  initialColor={resolvedColorButton}
                />
              </InputGroup>
              <InputGroup title="Button text color" noMargin>
                <ColorInput
                  name="color_button_text"
                  register={register}
                  initialColor={resolvedColorButtonText}
                />
              </InputGroup>
              <InputGroup title="Link color" noMargin>
                <ColorInput
                  name="color_link"
                  register={register}
                  initialColor={resolvedColorLink}
                />
                {ratio < 4.5 && (
                  <span className="u-font-size-x-small text-error">
                    Link color may be hard to read on white background based on WCAG standards.
                    Choose a color with better contrast for readability.
                  </span>
                )}
              </InputGroup>
            </div>
          </div>
          <DisplayBox show white>
            <ColorContrastCard
              foreground={resolvedColorButtonText}
              background={resolvedColorButton}
            />
          </DisplayBox>
        </article>
      </section>
    </BrandingTabSplitLayout>
  );
};
